import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import logoFull from 'figma:asset/fce628fa1ff2c0ce8e1c3b074b0a7732c9200683.png';

type Language = 'he' | 'en' | 'ru';

interface HeaderProps {
  language: Language;
  onLanguageChange: (lang: Language) => void;
}

const menuTranslations = {
  he: {
    home: 'בית',
    services: 'שירותים',
    gallery: 'גלריה',
    booking: 'קביעת תור',
    contact: 'יצירת קשר',
  },
  en: {
    home: 'Home',
    services: 'Services',
    gallery: 'Gallery',
    booking: 'Book Now',
    contact: 'Contact',
  },
  ru: {
    home: 'Главная',
    services: 'Услуги',
    gallery: 'Галерея',
    booking: 'Запись',
    contact: 'Контакты',
  },
};

export function Header({ language, onLanguageChange }: HeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const t = menuTranslations[language];
  const isRTL = language === 'he';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80; // Header height
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'shadow-lg' : ''
      }`}
      style={{
        backgroundColor: isScrolled ? 'rgba(244, 238, 215, 0.95)' : 'rgba(244, 238, 215, 0.9)',
        backdropFilter: 'blur(10px)',
      }}
    >
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <div 
            className="flex items-center cursor-pointer"
            onClick={() => scrollToSection('home')}
          >
            <img
              src={logoFull}
              alt="ShowGroom"
              className="h-10 sm:h-12 w-auto"
              width="120"
              height="48"
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6 lg:gap-8">
            <button
              onClick={() => scrollToSection('home')}
              className="hover:opacity-80 transition-opacity"
              style={{ color: '#444444' }}
            >
              {t.home}
            </button>
            <button
              onClick={() => scrollToSection('services')}
              className="hover:opacity-80 transition-opacity"
              style={{ color: '#444444' }}
            >
              {t.services}
            </button>
            <button
              onClick={() => scrollToSection('gallery')}
              className="hover:opacity-80 transition-opacity"
              style={{ color: '#444444' }}
            >
              {t.gallery}
            </button>
            {/* Booking temporarily hidden */}
            {/* <button
              onClick={() => scrollToSection('booking')}
              className="hover:opacity-80 transition-opacity"
              style={{ color: '#444444' }}
            >
              {t.booking}
            </button> */}
            <button
              onClick={() => scrollToSection('contact')}
              className="hover:opacity-80 transition-opacity"
              style={{ color: '#444444' }}
            >
              {t.contact}
            </button>

            {/* Language Switcher */}
            <div className="flex gap-2 ml-4">
              <button
                onClick={() => onLanguageChange('he')}
                className={`px-3 py-1 rounded-full text-sm transition-all ${
                  language === 'he' ? 'shadow-md' : 'opacity-70 hover:opacity-100'
                }`}
                style={{
                  backgroundColor: language === 'he' ? '#F29B70' : 'rgba(255, 255, 255, 0.5)',
                  color: '#444444',
                }}
              >
                עב
              </button>
              <button
                onClick={() => onLanguageChange('en')}
                className={`px-3 py-1 rounded-full text-sm transition-all ${
                  language === 'en' ? 'shadow-md' : 'opacity-70 hover:opacity-100'
                }`}
                style={{
                  backgroundColor: language === 'en' ? '#F29B70' : 'rgba(255, 255, 255, 0.5)',
                  color: '#444444',
                }}
              >
                EN
              </button>
              <button
                onClick={() => onLanguageChange('ru')}
                className={`px-3 py-1 rounded-full text-sm transition-all ${
                  language === 'ru' ? 'shadow-md' : 'opacity-70 hover:opacity-100'
                }`}
                style={{
                  backgroundColor: language === 'ru' ? '#F29B70' : 'rgba(255, 255, 255, 0.5)',
                  color: '#444444',
                }}
              >
                РУ
              </button>
            </div>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2"
            style={{ color: '#444444' }}
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <X size={24} aria-hidden="true" /> : <Menu size={24} aria-hidden="true" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div
            className="md:hidden pb-4"
            style={{ borderTop: '1px solid rgba(74, 59, 53, 0.1)' }}
          >
            <div className="flex flex-col gap-3 pt-4">
              <button
                onClick={() => scrollToSection('home')}
                className="text-left px-4 py-2 rounded-lg hover:shadow-md transition-all"
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.5)', color: '#444444' }}
              >
                {t.home}
              </button>
              <button
                onClick={() => scrollToSection('services')}
                className="text-left px-4 py-2 rounded-lg hover:shadow-md transition-all"
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.5)', color: '#444444' }}
              >
                {t.services}
              </button>
              <button
                onClick={() => scrollToSection('gallery')}
                className="text-left px-4 py-2 rounded-lg hover:shadow-md transition-all"
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.5)', color: '#444444' }}
              >
                {t.gallery}
              </button>
              {/* Booking temporarily hidden */}
              {/* <button
                onClick={() => scrollToSection('booking')}
                className="text-left px-4 py-2 rounded-lg hover:shadow-md transition-all"
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.5)', color: '#444444' }}
              >
                {t.booking}
              </button> */}
              <button
                onClick={() => scrollToSection('contact')}
                className="text-left px-4 py-2 rounded-lg hover:shadow-md transition-all"
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.5)', color: '#444444' }}
              >
                {t.contact}
              </button>

              {/* Mobile Language Switcher */}
              <div className="flex gap-2 px-4 pt-2">
                <button
                  onClick={() => onLanguageChange('he')}
                  className={`flex-1 py-2 rounded-full text-sm transition-all ${
                    language === 'he' ? 'shadow-md' : 'opacity-70 hover:opacity-100'
                  }`}
                  style={{
                    backgroundColor: language === 'he' ? '#F29B70' : 'rgba(255, 255, 255, 0.5)',
                    color: '#444444',
                  }}
                >
                  עברית
                </button>
                <button
                  onClick={() => onLanguageChange('en')}
                  className={`flex-1 py-2 rounded-full text-sm transition-all ${
                    language === 'en' ? 'shadow-md' : 'opacity-70 hover:opacity-100'
                  }`}
                  style={{
                    backgroundColor: language === 'en' ? '#F29B70' : 'rgba(255, 255, 255, 0.5)',
                    color: '#444444',
                  }}
                >
                  English
                </button>
                <button
                  onClick={() => onLanguageChange('ru')}
                  className={`flex-1 py-2 rounded-full text-sm transition-all ${
                    language === 'ru' ? 'shadow-md' : 'opacity-70 hover:opacity-100'
                  }`}
                  style={{
                    backgroundColor: language === 'ru' ? '#F29B70' : 'rgba(255, 255, 255, 0.5)',
                    color: '#444444',
                  }}
                >
                  Русский
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}